import React, { useState, useEffect } from 'react';
import { Subdivision, SubdivisionFormData } from '../types/subdivision';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface SubdivisionFormSliderProps {
  workspaceId: string;
  subdivisionId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Builder {
  _id: string;
  name: string;
}

const SubdivisionFormSlider: React.FC<SubdivisionFormSliderProps> = ({
  workspaceId,
  subdivisionId,
  onSuccess,
  onCancel
}) => {
  const [builders, setBuilders] = useState<Builder[]>([]);
  const [loadingBuilders, setLoadingBuilders] = useState(true);

  const initialFormData: SubdivisionFormData = {
    name: '',
    location: '',
    builderId: ''
  };

  useEffect(() => {
    fetchBuilders();
  }, [workspaceId]);

  const fetchBuilders = async () => {
    try {
      setLoadingBuilders(true);
      const response = await api.get(`/workspaces/${workspaceId}/builders`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setBuilders(response.data.builders);
    } catch (error) {
      console.error('Error fetching builders:', error);
    } finally {
      setLoadingBuilders(false);
    }
  };

  const validateForm = (data: SubdivisionFormData): string | null => {
    if (!data.name.trim()) {
      return 'Subdivision name is required';
    }
    if (!data.builderId) {
      return 'Builder selection is required';
    }
    return null;
  };

  const transformFetchData = (response: any): SubdivisionFormData => {
    const subdivision: Subdivision = response.subdivision;
    return {
      name: subdivision.name || '',
      location: subdivision.location || '',
      builderId: subdivision.builderId || ''
    };
  };

  const transformSubmitData = (data: SubdivisionFormData) => {
    return {
      name: data.name.trim(),
      location: data.location.trim() || undefined,
      builderId: data.builderId
    };
  };

  // Don't render the form until builders are loaded for new subdivisions
  if (!subdivisionId && loadingBuilders) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      workspaceId={workspaceId}
      itemId={subdivisionId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/subdivisions`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="subdivision-form"
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div className="form-group">
            <label htmlFor="builderId" className="form-label">
              Builder
            </label>
            {loadingBuilders ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading builders...</p>
              </div>
            ) : (
              <>
                <select
                  id="builderId"
                  name="builderId"
                  className="form-input"
                  value={formData.builderId}
                  onChange={handleInputChange}
                  required
                  disabled={loading || !!subdivisionId}
                >
                  <option value="">Select a builder</option>
                  {builders.map((builder) => (
                    <option key={builder._id} value={builder._id}>
                      {builder.name}
                    </option>
                  ))}
                </select>
                {subdivisionId && (
                  <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                    Builder cannot be changed after creation
                  </small>
                )}
              </>
            )}
          </div>

          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Subdivision Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Highland Estates"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="location" className="form-label">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              className="form-input"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="Main St & Oak Ave"
              disabled={loading}
            />
            <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
              Optional: General location or intersection
            </small>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default SubdivisionFormSlider;
